import type { ProductFeature } from '@/generated/productAnalytics.types'
import { router } from '@inertiajs/vue3'
import posthog from 'posthog-js'
import type { App } from 'vue'
import { dropNoisyExceptionEvents } from './errorNoise'

// Capture routes through our first-party reverse proxy (e.snitcher.com) so
// ad-blockers stop dropping events; the ui host keeps toolbar and in-app
// links pointing at the PostHog app rather than the proxy. app.ts imports
// the proxy host as its fallback so there is a single source of truth.
export const POSTHOG_PROXY_HOST = 'https://e.snitcher.com'
export const POSTHOG_UI_HOST = 'https://eu.posthog.com'

export interface PostHogConfig {
    apiKey: string
    apiHost?: string
    enableSessionRecording?: boolean
    enableHeatmaps?: boolean
    capturePageview?: boolean
    capturePageleave?: boolean
    isImpersonating?: boolean
}

let posthogInstance: typeof posthog | null = null
let impersonationActive = false

// Exposed so capture paths that talk to posthog-js directly (see
// lib/errorCapture.ts) can enforce the same no-capture-while-impersonating
// invariant instead of relying on the client happening to be unbooted.
export function isImpersonationActive(): boolean {
    return impersonationActive
}

export function initPostHog(config: PostHogConfig) {
    impersonationActive = config.isImpersonating ?? false

    // Impersonated sessions must capture NOTHING to PostHog. Not booting
    // posthog-js kills every capture path at once — autocapture, pageviews,
    // session replay, heatmaps, exception capture — and critically identify(),
    // which would irreversibly merge the staff browser's device activity into
    // the customer's person record. Impersonation always starts and ends with
    // a full page load (enter: Nova redirects to /dashboard, exit: the
    // stop-impersonating URL redirects back to Nova), so this init gate is the
    // primary guard; syncImpersonationState covers the multi-tab edge where
    // the shared session flips mid-flight.
    if (impersonationActive) {
        return null
    }

    if (!config.apiKey) {
        console.warn('PostHog API key not provided, skipping initialization')
        return null
    }

    // Initialize PostHog with configuration
    posthog.init(config.apiKey, {
        api_host: config.apiHost || POSTHOG_PROXY_HOST,
        ui_host: POSTHOG_UI_HOST,
        capture_pageview: config.capturePageview ?? false, // We'll handle this manually
        capture_pageleave: config.capturePageleave ?? true,
        autocapture: true, // Automatically capture clicks, form submissions, etc.
        disable_session_recording: !(config.enableSessionRecording ?? true),
        mask_all_text: false, // Don't mask text by default
        mask_all_element_attributes: false,
        enable_heatmaps: config.enableHeatmaps ?? true,
        capture_exceptions: {
            capture_unhandled_errors: true,
            capture_unhandled_rejections: true,
            capture_console_errors: false,
        },
        // Autocaptured $exception events skip lib/errorCapture.ts, so drop
        // no-signal browser/third-party noise (ResizeObserver, blocked gtag,
        // iOS webview handlers) here before it reaches the tracker.
        before_send: dropNoisyExceptionEvents,
        loaded: posthog => {
            // Identify if user is available
            const windowWithInertia = window as any
            const admin = windowWithInertia.Inertia?.page?.props?.admin

            // Skip identification when staff is impersonating
            if (admin?.is_impersonating) {
                return
            }

            const user = windowWithInertia.Inertia?.page?.props?.user

            if (user?.id) {
                // Identify user with properties
                posthog.identify(user.id.toString(), {
                    email: user.email,
                    name: user.name,
                    created_at: user.created_at,
                    is_admin: user.is_admin || false,
                    has_2fa_enabled: user.two_factor_enabled || false,
                })

                registerWorkspaceContext(posthog, user?.current_site)
            }
        },
    })

    posthogInstance = posthog

    // Every browser-originated event carries channel=web as a super property,
    // matching the backend's Laravel-Context stamp (web/api/mcp — see
    // app/Business/ProductAnalytics/Value/AnalyticsChannel.php) so that an
    // absent channel property means system-initiated across the whole project.
    posthog.register({ channel: 'web' })

    // Heal a stale opt-out left behind if a previous session flipped into
    // impersonation mid-flight (see syncImpersonationState) — the opt-out
    // persists in local storage and would otherwise silence the staff
    // member's own activity forever.
    if (posthog.has_opted_out_capturing()) {
        posthog.opt_in_capturing({ captureEventName: null })
    }

    // Set up Inertia navigation tracking
    setupInertiaTracking()

    return posthog
}

// Multi-tab edge case: the impersonation session is shared browser-wide, so a
// tab that booted PostHog as the staff member can see `admin.is_impersonating`
// appear on an Inertia response when the staff impersonates from another tab.
// Opting out stops autocapture, replay, and captures without a reload;
// opting back in (silently) restores normal tracking once impersonation ends.
function syncImpersonationState(isImpersonating: boolean) {
    if (isImpersonating === impersonationActive) {
        return
    }
    impersonationActive = isImpersonating

    if (!posthogInstance) {
        return
    }
    if (isImpersonating) {
        posthogInstance.opt_out_capturing()
    } else if (posthogInstance.has_opted_out_capturing()) {
        posthogInstance.opt_in_capturing({ captureEventName: null })
    }
}

// Structurally typed: receives either the module-level instance or the
// `loaded` callback's PostHogInterface param, and only needs register()/group().
function registerWorkspaceContext(
    ph: {
        register: (properties: Record<string, any>) => void
        group: (groupType: string, groupKey: string) => void
    },
    site: any
) {
    if (!site) return

    if (site.id) {
        // Same company-group key the backend stamps (PostHogService uses the
        // raw website id) — stringified, since posthog-js keys are strings.
        ph.group('company', String(site.id))
    }

    ph.register({
        workspace_lifecycle_stage: site.subscription_status,
        workspace_age_days: site.created_at_ts
            ? Math.floor((Date.now() / 1000 - site.created_at_ts) / 86400)
            : null,
        plan: site.plan,
    })
}

function setupInertiaTracking() {
    if (!posthogInstance) return

    // Track page views on Inertia navigation
    router.on('navigate', event => {
        const page = event.detail.page

        // Skip tracking when staff is impersonating
        const admin = page.props.admin as
            | { is_impersonating?: boolean }
            | undefined
        syncImpersonationState(admin?.is_impersonating ?? false)
        if (impersonationActive) {
            return
        }

        const url = window.location.href
        const path = window.location.pathname

        // Update user identification if user data changes — BEFORE capturing,
        // since group()/register() write super props that capture() reads at
        // call time: the pageview landing a workspace switch must already
        // carry the new workspace's $groups and context.
        const user = page.props.user as any

        if (user?.id) {
            posthogInstance.identify(user.id.toString(), {
                email: user.email,
                name: user.name,
                created_at: user.created_at,
                is_admin: user.is_admin || false,
                has_2fa_enabled: user.two_factor_enabled || false,
            })

            registerWorkspaceContext(posthogInstance, user?.current_site)
        }

        // Capture page view with additional context
        posthogInstance.capture('$pageview', {
            $current_url: url,
            $pathname: path,
            component: page.component,
            props: page.props,
        })

        // Track specific page views
        trackSpecificPageViews(path, page.component)
    })

    // Track onboarding completion
    router.on('success', event => {
        if (impersonationActive) {
            return
        }
        const path = window.location.pathname
        if (path === '/onboarding/success') {
            posthogInstance.capture('onboarding_completed')
        }
    })
}

function trackSpecificPageViews(path: string, component: string) {
    // Track onboarding steps
    if (path.includes('/onboarding/')) {
        const step = path.split('/').pop()
        posthogInstance?.capture('onboarding_step_viewed', {
            step: step,
            component: component,
        })
    }

    // Track feature page views (see featureForPath for the route map).
    const feature = featureForPath(path)
    if (feature !== null) {
        posthogInstance?.capture('Feature Viewed', {
            feature: feature,
        })
    }
}

// Maps a pathname to the canonical ProductFeature it represents. Only routes
// that map to a canonical ProductFeature are emitted — non-registry pages
// (dashboard, leads, settings, team, billing) rely on $pageview instead, so
// the feature vocabulary stays aligned with the backend registry. Exported
// for tests, which pin these patterns to the real route shapes.
export function featureForPath(path: string): ProductFeature | null {
    const featurePages: Array<[RegExp, ProductFeature]> = [
        [/^\/[^/]+\/segments(\/|$)/, 'segments'],
        [/^\/[^/]+\/automations(\/|$)/, 'automations'],
        [/^\/[^/]+\/analytics(\/|$)/, 'reports'],
        [/^\/[^/]+\/settings\/integrations(\/|$)/, 'integrations'],
        // The custom-events settings page lives at the root level (no
        // /{website} prefix — see routes/web.php custom-event-sources.page).
        [/^\/settings\/custom-events(\/|$)/, 'custom_events'],
    ]

    for (const [pattern, feature] of featurePages) {
        if (pattern.test(path)) {
            return feature
        }
    }

    return null
}

// Export functions for manual event tracking
export function trackEvent(
    eventName: string,
    properties?: Record<string, any>
) {
    if (impersonationActive) {
        return
    }
    if (!posthogInstance) {
        console.warn('PostHog not initialized')
        return
    }
    posthogInstance.capture(eventName, properties)
}

export function identifyUser(userId: string, properties?: Record<string, any>) {
    if (impersonationActive) {
        return
    }
    if (!posthogInstance) {
        console.warn('PostHog not initialized')
        return
    }
    posthogInstance.identify(userId, properties)
}

export function setUserGroup(
    groupType: string,
    groupId: string,
    properties?: Record<string, any>
) {
    if (impersonationActive) {
        return
    }
    if (!posthogInstance) {
        console.warn('PostHog not initialized')
        return
    }
    posthogInstance.group(groupType, groupId, properties)
}

export function trackOnboardingProgress(
    step: string,
    data?: Record<string, any>
) {
    trackEvent('onboarding_progress', {
        step,
        ...data,
    })
}

export function trackFeatureUsage(
    feature: ProductFeature,
    action: string,
    properties?: Record<string, any>
) {
    trackEvent('Feature Used', {
        feature,
        action,
        ...properties,
    })
}

// Vue plugin
export const PostHogPlugin = {
    install(app: App, options: PostHogConfig) {
        const instance = initPostHog(options)

        if (instance) {
            app.config.globalProperties.$posthog = instance
            app.provide('posthog', instance)
        }
    },
}

// Composable for using PostHog in components
export function usePostHog() {
    return {
        trackEvent,
        identifyUser,
        setUserGroup,
        trackOnboardingProgress,
        trackFeatureUsage,
        posthog: posthogInstance,
    }
}
